import { useEffect, useRef } from 'react';

import { SiteConfigManager } from '@atlas/core';

import { useLiveChatContextState } from 'components/LiveChat/LiveChatContext';
import {
  CHAT_READY_TIMEOUT_MS,
  classifyLiveChatDeepLink,
  DEEP_LINK_SESSION_KEY,
  removeLiveChatParam,
  waitForChatReady,
} from 'components/LiveChat/liveChatDeepLink';

const readSessionGuard = () => {
  try {
    return window.sessionStorage.getItem(DEEP_LINK_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const writeSessionGuard = () => {
  try {
    window.sessionStorage.setItem(DEEP_LINK_SESSION_KEY, '1');
  } catch {
    // Ignore storage errors: the in-mount ref guard still prevents re-open.
  }
};

const stripDeepLinkParam = () => {
  try {
    const { hash, pathname, search } = window.location;
    const nextSearch = removeLiveChatParam(search);
    const queryString = nextSearch ? `?${nextSearch}` : '';
    const nextUrl = `${pathname}${queryString}${hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // Ignore history errors: param removal is best-effort, chat already opened.
  }
};

/**
 * Orchestration hook for the LiveChat deep-link feature (akira/desktop).
 *
 * On mount (once per session) it inspects `window.location.search` for the
 * reserved deep-link parameter. When present and chat is available + configured
 * (desktop config), it waits for the provider to reach readiness (30s budget),
 * issues a single deterministic `openChat()`, and removes the parameter from the
 * URL via `history.replaceState`. It uses `useLiveChatContextState()` directly
 * (akira has no mounted LiveChat provider) and only touches window.location /
 * window.history. All provider/history/storage access is guarded so it never
 * surfaces a chat error.
 */
const useLiveChatDeepLink = () => {
  const { openChat } = useLiveChatContextState();
  const hasHandledRef = useRef(false);

  useEffect(() => {
    if (!classifyLiveChatDeepLink(window.location.search)) {
      return undefined;
    }

    if (hasHandledRef.current || readSessionGuard()) {
      return undefined;
    }

    const isChatAvailable = SiteConfigManager.getConfig(
      'config.config.support.live_chat.desktop.is_available',
    );
    const chatType = SiteConfigManager.getConfig('config.config.support.live_chat.desktop.type');

    if (!isChatAvailable || !chatType) {
      return undefined;
    }

    let removalTimer = null;

    const cleanupReady = waitForChatReady(chatType, {
      onReady: () => {
        hasHandledRef.current = true;
        writeSessionGuard();

        try {
          openChat();
        } catch {
          // Provider open failures must never surface a chat error.
        }

        removalTimer = setTimeout(stripDeepLinkParam, 0);
      },
      onTimeout: () => {
        // Not ready within the budget: leave the widget collapsed, no error.
      },
      timeoutMs: CHAT_READY_TIMEOUT_MS,
    });

    return () => {
      cleanupReady();
      if (removalTimer !== null) {
        clearTimeout(removalTimer);
        removalTimer = null;
      }
    };
    // Mount-once orchestration: intentionally empty dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useLiveChatDeepLink;
